import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { qaHref } from '../contextLinks';

// CR-015 (SQA-36): review/promotion queue for Q&A candidates mined by the
// SQA-35 pipeline. The reviewer (Erik) is the arbiter — promote creates a
// canonical qa_bank row, merge folds into an existing one, flag-conflict
// blocks publish. Clones the keyboard-driven review UX (J/K, P/R).
//
// SQA-60: Added "Brief Candidates" tab to surface unmatched questions from
// project-brief preflights for review/promotion.

const INTENTS = [
  'faq',
  'objection',
  'spec_clarification',
  'pricing',
  'operation',
  'competitive',
  'attribution',
  'product_request',
] as const;

interface Candidate {
  id: string;
  extraction_run_id: string;
  candidate_question: string;
  candidate_answer: string | null;
  suggested_intent: string | null;
  suggested_product_tags: string[] | null;
  source_ref: string | null;
  source_quote: string | null;
  confidence: number | null;
  reason: string | null;
  review_status: string;
}

interface BriefOpenQuestion {
  id: string;
  question: string;
  intent: string;
  customer_context: string | null;
  asker_role: string | null;
  citations: string[];
  qa_bank_id: string | null;
  frequency: number;
  last_asked: string;
  suggested_for_bank: boolean;
  bank_match_confidence: number | null;
}

interface QABankCandidate {
  intent: string;
  sample_questions: BriefOpenQuestion[];
  qa_bank_ready: boolean;
  intent_frequency: number;
  most_recent: string;
  avg_confidence: number | null;
  actions: Array<{ label: string; href?: string; action?: string }>;
}

type Banner = { kind: 'ok' | 'err'; text: string } | null;

function intentLabel(value: string | null): string {
  if (!value) return 'Unclassified';
  return value
    .split('_')
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ');
}

function formatDate(value: string | null): string {
  if (!value) return 'No date';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatPercent(value: number | null): string {
  if (value == null) return 'No score';
  return `${Math.round(value * 100)}%`;
}

export function QaBankReview(): JSX.Element {
  const [activeTab, setActiveTab] = useState<'extraction' | 'brief'>('extraction');
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [briefCandidates, setBriefCandidates] = useState<QABankCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(0);
  const [expandedIntent, setExpandedIntent] = useState<string | null>(null);
  const [banner, setBanner] = useState<Banner>(null);
  const [busy, setBusy] = useState(false);

  // Editable fields for the selected candidate.
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [intent, setIntent] = useState<string>('faq');
  const [visibility, setVisibility] = useState<'internal' | 'public_eligible'>('internal');
  const [confidence, setConfidence] = useState<'high' | 'medium' | 'low'>('medium');

  const editing = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === 'extraction') {
        const res = await fetch('/api/qa-extraction/candidates?status=pending&limit=200', {
          credentials: 'include',
        });
        if (!res.ok) throw new Error(`status ${res.status}`);
        const data = (await res.json()) as { candidates: Candidate[] };
        setCandidates(data.candidates);
        setSelected(0);
      } else {
        const res = await fetch('/api/brief-candidates', {
          credentials: 'include',
        });
        if (!res.ok) throw new Error(`status ${res.status}`);
        const data = (await res.json()) as { qa_bank_candidates: QABankCandidate[] };
        setBriefCandidates(data.qa_bank_candidates);
        setExpandedIntent(null);
      }
    } catch (err) {
      setBanner({ kind: 'err', text: `Failed to load: ${(err as Error).message}` });
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    void load();
  }, [load]);

  const current = candidates[selected];

  // Sync editable fields when selection changes.
  useEffect(() => {
    if (!current) return;
    setQuestion(current.candidate_question);
    setAnswer(current.candidate_answer ?? '');
    setIntent(current.suggested_intent ?? 'faq');
    setVisibility('internal');
    setConfidence('medium');
  }, [current?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = useCallback(
    async (path: string, body: Record<string, unknown>, okMsg: string) => {
      if (!current || busy) return;
      setBusy(true);
      try {
        const res = await fetch(`/api/qa-review/candidates/${current.id}/${path}`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const payload = (await res.json().catch(() => ({}))) as Record<string, unknown>;
        if (res.status === 409) {
          const existing = payload.existing as { id?: string; canonical_question?: string } | undefined;
          setBanner({
            kind: 'err',
            text: `Duplicate question already canonical (row ${existing?.id ?? '?'}). Use Merge instead.`,
          });
          return;
        }
        if (!res.ok) throw new Error(String(payload.error ?? `status ${res.status}`));
        setBanner({ kind: 'ok', text: okMsg });
        // Drop the actioned candidate from the queue, keep position.
        setCandidates((prev) => prev.filter((c) => c.id !== current.id));
        setSelected((s) => Math.max(0, Math.min(s, candidates.length - 2)));
      } catch (err) {
        setBanner({ kind: 'err', text: (err as Error).message });
      } finally {
        setBusy(false);
      }
    },
    [current, busy, candidates.length],
  );

  const promote = useCallback(
    () =>
      act(
        'promote',
        {
          canonical_question: question,
          canonical_answer: answer,
          intent,
          visibility,
          answer_confidence: confidence,
          product_tags: current?.suggested_product_tags ?? [],
        },
        'Promoted to canonical qa_bank row.',
      ),
    [act, question, answer, intent, visibility, confidence, current],
  );

  const reject = useCallback(() => act('reject', {}, 'Rejected.'), [act]);

  const merge = useCallback(() => {
    const target = window.prompt('Merge into which qa_bank row id?');
    const targetId = target ? Number(target) : 0;
    if (!targetId) return;
    void act('merge', { target_qa_bank_id: targetId }, `Merged into row ${targetId}.`);
  }, [act]);

  const flagConflict = useCallback(() => {
    const note = window.prompt('Conflict note (what disagrees?)');
    if (!note) return;
    const target = window.prompt('Conflicting existing qa_bank row id? (blank to just park)');
    void act(
      'flag-conflict',
      { conflict_note: note, target_qa_bank_id: target ? Number(target) : undefined },
      'Flagged — routed to needs_erik.',
    );
  }, [act]);

  const skipBriefCandidate = useCallback(
    async (intent: string) => {
      if (busy) return;
      setBusy(true);
      try {
        // Mark all questions in this intent group as suggested_for_bank=false
        const candidate = briefCandidates.find((c) => c.intent === intent);
        if (!candidate) return;
        
        for (const q of candidate.sample_questions) {
          const res = await fetch(`/api/brief-candidates/${q.id}/skip`, {
            method: 'POST',
            credentials: 'include',
          });
          if (!res.ok) throw new Error(`status ${res.status}`);
        }
        
        setBanner({ kind: 'ok', text: `Skipped ${intent} candidates.` });
        setBriefCandidates((prev) => prev.filter((c) => c.intent !== intent));
      } catch (err) {
        setBanner({ kind: 'err', text: (err as Error).message });
      } finally {
        setBusy(false);
      }
    },
    [briefCandidates, busy],
  );

  const promoteBriefCandidate = useCallback(
    (intentGroup: QABankCandidate) => {
      // Pre-fill new qa_bank form with intent group data
      const sampleQ = intentGroup.sample_questions[0];
      if (!sampleQ) return;
      
      // Navigate to promote form (in reality, would use router or modal)
      const citations = intentGroup.sample_questions.flatMap((q) => q.citations).join(', ');
      const question = intentGroup.sample_questions.map((q) => q.question).join(' / ');
      
      // For now, use window.alert as placeholder for form navigation
      window.alert(
        `Promote to Q&A Bank:\n\nIntent: ${intentGroup.intent}\nQuestions: ${question}\nCitations: ${citations}\n\nImplement full form in follow-up work.`,
      );
    },
    [],
  );

  const deleteBriefQuestion = useCallback(
    async (questionId: string, intent: string) => {
      if (busy) return;
      const confirmed = window.confirm(
        'Hard-delete this question as invalid? This action cannot be undone (audit trail logged).',
      );
      if (!confirmed) return;

      setBusy(true);
      try {
        const res = await fetch(`/api/brief-candidates/${questionId}`, {
          method: 'DELETE',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: 'invalid_question' }),
        });
        if (!res.ok) throw new Error(`status ${res.status}`);

        setBanner({ kind: 'ok', text: 'Question deleted.' });
        // Reload to refresh the list
        await load();
      } catch (err) {
        setBanner({ kind: 'err', text: (err as Error).message });
      } finally {
        setBusy(false);
      }
    },
    [busy, load],
  );

  // Keyboard shortcuts (ignored while typing in a field, only for extraction tab).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (editing.current || activeTab !== 'extraction') return;
      if (e.key === 'j') setSelected((s) => Math.min(s + 1, candidates.length - 1));
      else if (e.key === 'k') setSelected((s) => Math.max(s - 1, 0));
      else if (e.key === 'p') void promote();
      else if (e.key === 'r') void reject();
      else if (e.key === 'm') merge();
      else if (e.key === 'c') flagConflict();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeTab, candidates.length, promote, reject, merge, flagConflict]);

  const extractionMetrics = useMemo(() => {
    return {
      pending: candidates.length,
      selected: current ? selected + 1 : 0,
      intent: intentLabel(current?.suggested_intent ?? null),
      confidence: formatPercent(current?.confidence ?? null),
    };
  }, [candidates.length, current, selected]);

  const briefMetrics = useMemo(() => {
    return {
      groups: briefCandidates.length,
      samples: briefCandidates.reduce((sum, group) => sum + group.sample_questions.length, 0),
      frequency: briefCandidates.reduce((sum, group) => sum + group.intent_frequency, 0),
    };
  }, [briefCandidates]);

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-5 sm:px-6">
      <div className="mx-auto max-w-7xl">
      <header className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Review work
          </p>
          <h1 className="text-2xl font-semibold text-slate-950">Q&amp;A Bank review</h1>
          <p className="mt-1 text-sm leading-6 text-slate-600">
            Decide what becomes reusable customer knowledge. Promote clean answers,
            merge duplicates, park conflicts, and screen recurring Project Brief gaps
            before they become canonical.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 sm:w-auto"
        >
          Refresh
        </button>
      </header>

      <div className="mb-4 grid gap-3 lg:grid-cols-2">
        <button
          type="button"
          onClick={() => setActiveTab('extraction')}
          className={`rounded-lg border p-4 text-left transition-colors ${
            activeTab === 'extraction'
              ? 'border-slate-900 bg-white text-slate-950 shadow-sm'
              : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
          }`}
        >
          <span className="block text-sm font-semibold">Candidate canonicalization</span>
          <span className="mt-1 block text-xs leading-5 text-slate-500">
            Transcript-mined Q&amp;A waiting for promote, merge, reject, or conflict review.
          </span>
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('brief')}
          className={`rounded-lg border p-4 text-left transition-colors ${
            activeTab === 'brief'
              ? 'border-slate-900 bg-white text-slate-950 shadow-sm'
              : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
          }`}
        >
          <span className="block text-sm font-semibold">Project Brief gaps</span>
          <span className="mt-1 block text-xs leading-5 text-slate-500">
            Recurring unmatched brief questions that may deserve reusable answers.
          </span>
        </button>
      </div>

      {banner && (
        <div
          className={`mb-3 rounded-md px-3 py-2 text-sm ${
            banner.kind === 'ok'
              ? 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200'
              : 'bg-rose-50 text-rose-800 ring-1 ring-rose-200'
          }`}
        >
          {banner.text}
        </div>
      )}

      {activeTab === 'extraction' ? (
        <>
          <section className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
              <div className="text-xs font-medium text-slate-500">Pending</div>
              <div className="mt-1 text-2xl font-semibold text-slate-950">
                {loading ? '—' : extractionMetrics.pending}
              </div>
            </div>
            <div className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
              <div className="text-xs font-medium text-slate-500">Selected</div>
              <div className="mt-1 text-2xl font-semibold text-slate-950">
                {loading || extractionMetrics.selected === 0 ? '—' : extractionMetrics.selected}
              </div>
            </div>
            <div className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
              <div className="text-xs font-medium text-slate-500">Suggested intent</div>
              <div className="mt-2 truncate text-sm font-semibold text-slate-950">
                {loading ? '—' : extractionMetrics.intent}
              </div>
            </div>
            <div className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
              <div className="text-xs font-medium text-slate-500">Confidence</div>
              <div className="mt-2 text-sm font-semibold text-slate-950">
                {loading ? '—' : extractionMetrics.confidence}
              </div>
            </div>
          </section>

          {!loading && candidates.length === 0 ? (
            <div className="rounded-lg bg-white p-8 text-slate-500 ring-1 ring-slate-200">
              Queue is clear. No pending Q&amp;A candidates need canonical review.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[22rem_minmax(0,1fr)]">
              <section className="rounded-lg bg-white ring-1 ring-slate-200">
                <div className="border-b border-slate-100 px-4 py-3">
                  <h2 className="text-sm font-semibold text-slate-950">Candidate queue</h2>
                  <p className="text-xs text-slate-500">
                    Pick one candidate, then decide its knowledge-bank outcome.
                  </p>
                </div>
                <ul className="max-h-[70vh] divide-y divide-slate-100 overflow-auto">
                  {candidates.map((c, i) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => setSelected(i)}
                        className={`w-full px-4 py-3 text-left text-sm ${
                          i === selected
                            ? 'bg-slate-900 text-white'
                            : 'text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        <div className="line-clamp-2 font-medium">{c.candidate_question}</div>
                        <div
                          className={`mt-1 flex flex-wrap gap-2 text-xs ${
                            i === selected ? 'text-slate-300' : 'text-slate-400'
                          }`}
                        >
                          <span>{intentLabel(c.suggested_intent)}</span>
                          <span>{formatPercent(c.confidence)}</span>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>

              {current && (
                <section className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
                  <div className="mb-4 flex flex-col gap-2 border-b border-slate-100 pb-4 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <h2 className="text-sm font-semibold text-slate-950">
                        Canonicalize candidate {selected + 1} of {candidates.length}
                      </h2>
                      <p className="text-xs text-slate-500">
                        Edit the reusable version before writing to `qa_bank`.
                      </p>
                    </div>
                    <Link
                      to={qaHref(current.candidate_question)}
                      className="rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                    >
                      Ask evidence
                    </Link>
                  </div>

                  <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_20rem]">
                    <div className="space-y-4">
                      <label className="block">
                        <span className="text-xs font-medium text-slate-500">
                          Canonical question
                        </span>
                        <textarea
                          value={question}
                          onChange={(e) => setQuestion(e.target.value)}
                          onFocus={() => (editing.current = true)}
                          onBlur={() => (editing.current = false)}
                          rows={2}
                          className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-900"
                        />
                      </label>
                      <label className="block">
                        <span className="text-xs font-medium text-slate-500">
                          Canonical answer
                        </span>
                        <textarea
                          value={answer}
                          onChange={(e) => setAnswer(e.target.value)}
                          onFocus={() => (editing.current = true)}
                          onBlur={() => (editing.current = false)}
                          rows={6}
                          className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-900"
                        />
                      </label>

                      <div className="grid gap-3 md:grid-cols-3">
                        <label className="text-sm">
                          <span className="block text-xs font-medium text-slate-500">Intent</span>
                          <select
                            value={intent}
                            onChange={(e) => setIntent(e.target.value)}
                            className="mt-1 w-full rounded-md border border-slate-200 px-2 py-2 text-sm"
                          >
                            {INTENTS.map((i) => (
                              <option key={i} value={i}>
                                {intentLabel(i)}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="text-sm">
                          <span className="block text-xs font-medium text-slate-500">
                            Visibility
                          </span>
                          <select
                            value={visibility}
                            onChange={(e) =>
                              setVisibility(e.target.value as 'internal' | 'public_eligible')
                            }
                            className="mt-1 w-full rounded-md border border-slate-200 px-2 py-2 text-sm"
                          >
                            <option value="internal">Internal</option>
                            <option value="public_eligible">Public eligible</option>
                          </select>
                        </label>
                        <label className="text-sm">
                          <span className="block text-xs font-medium text-slate-500">
                            Answer confidence
                          </span>
                          <select
                            value={confidence}
                            onChange={(e) =>
                              setConfidence(e.target.value as 'high' | 'medium' | 'low')
                            }
                            className="mt-1 w-full rounded-md border border-slate-200 px-2 py-2 text-sm"
                          >
                            <option value="high">High</option>
                            <option value="medium">Medium</option>
                            <option value="low">Low</option>
                          </select>
                        </label>
                      </div>

                      <div className="flex flex-wrap gap-2 pt-1">
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void promote()}
                          className="rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                        >
                          Promote
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={merge}
                          className="rounded-md bg-sky-600 px-3 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50"
                        >
                          Merge
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={flagConflict}
                          className="rounded-md bg-amber-500 px-3 py-2 text-sm font-medium text-white hover:bg-amber-600 disabled:opacity-50"
                        >
                          Flag conflict
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void reject()}
                          className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                        >
                          Reject
                        </button>
                      </div>
                    </div>

                    <aside className="space-y-3">
                      <div className="rounded-md bg-slate-50 p-3 text-xs leading-5 text-slate-600 ring-1 ring-slate-200">
                        <div>
                          <span className="font-semibold text-slate-900">Source:</span>{' '}
                          {current.source_ref ?? 'Unknown'}
                        </div>
                        {current.source_quote ? (
                          <blockquote className="mt-2 border-l-2 border-slate-300 pl-3 italic">
                            {current.source_quote}
                          </blockquote>
                        ) : null}
                        {current.reason ? (
                          <div className="mt-2">
                            <span className="font-semibold text-slate-900">Why surfaced:</span>{' '}
                            {current.reason}
                          </div>
                        ) : null}
                      </div>
                      <details className="rounded-md border border-slate-200 bg-white p-3">
                        <summary className="cursor-pointer text-xs font-semibold text-slate-700">
                          Keyboard shortcuts
                        </summary>
                        <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-slate-500">
                          <span>J / K: Move</span>
                          <span>P: Promote</span>
                          <span>R: Reject</span>
                          <span>M: Merge</span>
                          <span>C: Conflict</span>
                        </div>
                      </details>
                    </aside>
                  </div>
                </section>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          <section className="mb-4 grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
              <div className="text-xs font-medium text-slate-500">Intent groups</div>
              <div className="mt-1 text-2xl font-semibold text-slate-950">
                {loading ? '—' : briefMetrics.groups}
              </div>
            </div>
            <div className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
              <div className="text-xs font-medium text-slate-500">Sample questions</div>
              <div className="mt-1 text-2xl font-semibold text-slate-950">
                {loading ? '—' : briefMetrics.samples}
              </div>
            </div>
            <div className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
              <div className="text-xs font-medium text-slate-500">Total frequency</div>
              <div className="mt-1 text-2xl font-semibold text-slate-950">
                {loading ? '—' : briefMetrics.frequency}
              </div>
            </div>
          </section>

          {!loading && briefCandidates.length === 0 ? (
            <div className="rounded-lg bg-white p-8 text-slate-500 ring-1 ring-slate-200">
              No Project Brief gaps need review.
            </div>
          ) : (
            <div className="space-y-4">
              {briefCandidates.map((candidate) => (
                <section
                  key={candidate.intent}
                  className="overflow-hidden rounded-lg bg-white ring-1 ring-slate-200"
                >
                  <div className="flex flex-col gap-3 border-b border-slate-100 p-4 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <h2 className="text-base font-semibold text-slate-950">
                        {intentLabel(candidate.intent)}
                      </h2>
                      <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-600">
                        <span className="rounded-md bg-slate-100 px-2 py-1">
                          Asked {candidate.intent_frequency} times
                        </span>
                        <span className="rounded-md bg-slate-100 px-2 py-1">
                          Last asked {formatDate(candidate.most_recent)}
                        </span>
                        <span className="rounded-md bg-slate-100 px-2 py-1">
                          Avg confidence {formatPercent(candidate.avg_confidence)}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedIntent(expandedIntent === candidate.intent ? null : candidate.intent)
                      }
                      className="rounded-md border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                    >
                      {expandedIntent === candidate.intent ? 'Hide samples' : 'Review samples'}
                    </button>
                  </div>

                  <div className="space-y-3 p-4">
                    {(expandedIntent === candidate.intent
                      ? candidate.sample_questions
                      : candidate.sample_questions.slice(0, 1)
                    ).map((q) => (
                      <article
                        key={q.id}
                        className="rounded-md bg-slate-50 p-3 text-sm ring-1 ring-slate-200"
                      >
                        <div className="font-medium leading-6 text-slate-950">{q.question}</div>
                        <div className="mt-2 space-y-1 text-xs text-slate-600">
                            {q.customer_context && (
                              <div>
                                <strong>Context:</strong> {q.customer_context}
                              </div>
                            )}
                            {q.asker_role && (
                              <div>
                                <strong>Asker role:</strong> {q.asker_role}
                              </div>
                            )}
                            {q.citations.length > 0 && (
                              <div>
                                <strong>Citations:</strong> {q.citations.join(', ')}
                              </div>
                            )}
                            <div>
                              <strong>Frequency:</strong> {q.frequency} ·{' '}
                              <strong>Last asked:</strong> {formatDate(q.last_asked)}
                              {q.bank_match_confidence != null && (
                                <>
                                  {' '}
                                  · <strong>Confidence:</strong>{' '}
                                  {formatPercent(q.bank_match_confidence)}
                                </>
                              )}
                            </div>
                          </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <Link
                            to={qaHref(q.question)}
                            className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                          >
                            Ask evidence
                          </Link>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => void deleteBriefQuestion(q.id, candidate.intent)}
                            className="rounded-md border border-rose-200 bg-white px-2.5 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                          >
                            Invalid question
                          </button>
                        </div>
                      </article>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-2 border-t border-slate-100 p-4">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => promoteBriefCandidate(candidate)}
                      className="rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                    >
                      Promote to Bank
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void skipBriefCandidate(candidate.intent)}
                      className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                    >
                      Skip for now
                    </button>
                  </div>
                </section>
              ))}
            </div>
          )}
        </>
      )}
      </div>
    </div>
  );
}
